/**
 * Fix guidance data for common accessibility issues.
 * Maps ruleId to detailed information about the issue and how to fix it.
 */

export interface WcagCriterion {
  id: string;
  name: string;
  level: 'A' | 'AA' | 'AAA';
  url: string;
}

export interface CodeExample {
  title: string;
  before: string;
  after: string;
  language: 'html' | 'css' | 'javascript';
}

export interface ExternalResource {
  title: string;
  url: string;
  source: 'MDN' | 'WCAG' | 'Deque' | 'WebAIM' | 'Other';
}

export interface FixGuidance {
  ruleId: string;
  title: string;
  description: string;
  impact: 'critical' | 'serious' | 'moderate' | 'minor';
  wcagCriteria: WcagCriterion[];
  whyItMatters: string;
  howToFix: string;
  codeExamples: CodeExample[];
  externalResources: ExternalResource[];
}

export const fixGuidanceData: Record<string, FixGuidance> = {
  'image-alt': {
    ruleId: 'image-alt',
    title: 'Images must have alternate text',
    description:
      'Ensures `<img>` elements have alternate text or a role of none or presentation.',
    impact: 'critical',
    wcagCriteria: [
      {
        id: '1.1.1',
        name: 'Non-text Content',
        level: 'A',
        url: 'https://www.w3.org/WAI/WCAG21/Understanding/non-text-content.html',
      },
    ],
    whyItMatters: `Images without alternative text are inaccessible to users who rely on screen readers.
When an image lacks alt text, screen readers may announce the image filename or skip it entirely,
leaving users without important visual information. This affects blind users, users with low vision,
and users who have images disabled.`,
    howToFix: `Add an \`alt\` attribute to every \`<img>\` element:

- **Informative images**: Describe the content and function of the image concisely.
- **Decorative images**: Use an empty alt attribute (\`alt=""\`) to tell screen readers to skip it.
- **Functional images** (e.g., linked images): Describe the action or destination.
- **Complex images** (charts, graphs): Provide a brief alt text and link to a longer description.

Keep alt text concise (typically under 125 characters) and avoid phrases like "image of" or "picture of".`,
    codeExamples: [
      {
        title: 'Informative image',
        before: '<img src="puppy.jpg">',
        after: '<img src="puppy.jpg" alt="Golden retriever puppy playing in the park">',
        language: 'html',
      },
      {
        title: 'Decorative image',
        before: '<img src="decorative-border.png">',
        after: '<img src="decorative-border.png" alt="">',
        language: 'html',
      },
      {
        title: 'Functional image (link)',
        before:
          '<a href="/home"><img src="logo.png"></a>',
        after:
          '<a href="/home"><img src="logo.png" alt="Company Name - Go to homepage"></a>',
        language: 'html',
      },
    ],
    externalResources: [
      {
        title: 'WAI Images Tutorial',
        url: 'https://www.w3.org/WAI/tutorials/images/',
        source: 'WCAG',
      },
      {
        title: 'MDN: HTML img element',
        url: 'https://developer.mozilla.org/en-US/docs/Web/HTML/Element/img',
        source: 'MDN',
      },
      {
        title: 'Deque: Image Alt Text Best Practices',
        url: 'https://www.deque.com/blog/great-alt-text-introduction/',
        source: 'Deque',
      },
      {
        title: 'WebAIM: Alternative Text',
        url: 'https://webaim.org/techniques/alttext/',
        source: 'WebAIM',
      },
    ],
  },

  label: {
    ruleId: 'label',
    title: 'Form elements must have labels',
    description:
      'Ensures every form element has a label. Form elements include `<input>`, `<select>`, and `<textarea>`.',
    impact: 'critical',
    wcagCriteria: [
      {
        id: '1.3.1',
        name: 'Info and Relationships',
        level: 'A',
        url: 'https://www.w3.org/WAI/WCAG21/Understanding/info-and-relationships.html',
      },
      {
        id: '4.1.2',
        name: 'Name, Role, Value',
        level: 'A',
        url: 'https://www.w3.org/WAI/WCAG21/Understanding/name-role-value.html',
      },
    ],
    whyItMatters: `Form labels are essential for accessibility. Without proper labels:
- Screen reader users won't know what information to enter in form fields
- Users with cognitive disabilities may not understand the purpose of inputs
- Voice control users can't easily navigate to and interact with form fields
- The clickable area for focusing the input is reduced (labels expand this area)`,
    howToFix: `Associate a label with each form control using one of these methods:

1. **Explicit label** (recommended): Use the \`for\` attribute matching the input's \`id\`
2. **Implicit label**: Wrap the input inside the \`<label>\` element
3. **aria-label**: Use when a visible label isn't appropriate
4. **aria-labelledby**: Reference another element's text as the label

Always prefer visible labels over ARIA attributes when possible.`,
    codeExamples: [
      {
        title: 'Explicit label (recommended)',
        before: '<input type="text" id="email">',
        after:
          '<label for="email">Email address</label>\n<input type="text" id="email">',
        language: 'html',
      },
      {
        title: 'Implicit label',
        before: '<input type="checkbox">',
        after:
          '<label>\n  <input type="checkbox">\n  I agree to the terms\n</label>',
        language: 'html',
      },
      {
        title: 'Using aria-label (for icon buttons)',
        before: '<input type="search">',
        after: '<input type="search" aria-label="Search products">',
        language: 'html',
      },
    ],
    externalResources: [
      {
        title: 'MDN: HTML label element',
        url: 'https://developer.mozilla.org/en-US/docs/Web/HTML/Element/label',
        source: 'MDN',
      },
      {
        title: 'WAI Forms Tutorial',
        url: 'https://www.w3.org/WAI/tutorials/forms/labels/',
        source: 'WCAG',
      },
      {
        title: 'Deque: Form Labels',
        url: 'https://dequeuniversity.com/rules/axe/4.6/label',
        source: 'Deque',
      },
    ],
  },

  'html-has-lang': {
    ruleId: 'html-has-lang',
    title: '<html> element must have a lang attribute',
    description:
      'Ensures every HTML document has a lang attribute on the `<html>` element.',
    impact: 'serious',
    wcagCriteria: [
      {
        id: '3.1.1',
        name: 'Language of Page',
        level: 'A',
        url: 'https://www.w3.org/WAI/WCAG21/Understanding/language-of-page.html',
      },
    ],
    whyItMatters: `The lang attribute is critical for accessibility:
- Screen readers use it to select the correct voice profile and pronunciation
- Translation tools use it to determine the source language
- Search engines use it for language-specific indexing
- Browsers use it for language-specific rendering (quotes, hyphenation)

Without it, assistive technologies may mispronounce content or fail to process it correctly.`,
    howToFix: `Add a \`lang\` attribute to the \`<html>\` element with a valid BCP 47 language tag:

- Use two-letter ISO 639-1 codes when possible (e.g., "en", "fr", "de")
- Add region subtags for regional variants (e.g., "en-US", "en-GB", "pt-BR")
- Ensure the language tag matches the primary language of the page content`,
    codeExamples: [
      {
        title: 'English page',
        before: '<!DOCTYPE html>\n<html>\n  <head>...</head>\n</html>',
        after: '<!DOCTYPE html>\n<html lang="en">\n  <head>...</head>\n</html>',
        language: 'html',
      },
      {
        title: 'British English page',
        before: '<html>',
        after: '<html lang="en-GB">',
        language: 'html',
      },
      {
        title: 'French page',
        before: '<html>',
        after: '<html lang="fr">',
        language: 'html',
      },
    ],
    externalResources: [
      {
        title: 'MDN: Global lang attribute',
        url: 'https://developer.mozilla.org/en-US/docs/Web/HTML/Global_attributes/lang',
        source: 'MDN',
      },
      {
        title: 'W3C Choosing Language Tags',
        url: 'https://www.w3.org/International/questions/qa-choosing-language-tags',
        source: 'WCAG',
      },
      {
        title: 'Deque: html-has-lang',
        url: 'https://dequeuniversity.com/rules/axe/4.6/html-has-lang',
        source: 'Deque',
      },
    ],
  },

  'document-title': {
    ruleId: 'document-title',
    title: 'Documents must have a <title> element',
    description:
      'Ensures each HTML document contains a non-empty `<title>` element.',
    impact: 'serious',
    wcagCriteria: [
      {
        id: '2.4.2',
        name: 'Page Titled',
        level: 'A',
        url: 'https://www.w3.org/WAI/WCAG21/Understanding/page-titled.html',
      },
    ],
    whyItMatters: `The page title is often the first thing announced by screen readers and is essential for:
- Helping users identify the page they're on
- Distinguishing between multiple open tabs or windows
- Providing context in browser history and bookmarks
- Improving search engine optimization

A missing or generic title makes it difficult for users to navigate and understand page content.`,
    howToFix: `Add a descriptive \`<title>\` element inside the \`<head>\`:

- Make titles unique and descriptive for each page
- Put the most important/specific information first
- Keep titles concise (50-60 characters recommended)
- Consider the format: "Page Name - Section - Site Name"
- Avoid starting every title with the site name`,
    codeExamples: [
      {
        title: 'Adding a page title',
        before:
          '<head>\n  <meta charset="UTF-8">\n</head>',
        after:
          '<head>\n  <meta charset="UTF-8">\n  <title>Contact Us - Acme Corporation</title>\n</head>',
        language: 'html',
      },
      {
        title: 'Dynamic page title (React)',
        before: 'function ProductPage() {\n  return <div>...</div>;\n}',
        after:
          'function ProductPage({ product }) {\n  useEffect(() => {\n    document.title = `${product.name} - Our Store`;\n  }, [product.name]);\n  return <div>...</div>;\n}',
        language: 'javascript',
      },
    ],
    externalResources: [
      {
        title: 'MDN: HTML title element',
        url: 'https://developer.mozilla.org/en-US/docs/Web/HTML/Element/title',
        source: 'MDN',
      },
      {
        title: 'WebAIM: Page Titles',
        url: 'https://webaim.org/techniques/pagetitle/',
        source: 'WebAIM',
      },
      {
        title: 'Deque: document-title',
        url: 'https://dequeuniversity.com/rules/axe/4.6/document-title',
        source: 'Deque',
      },
    ],
  },

  'link-name': {
    ruleId: 'link-name',
    title: 'Links must have discernible text',
    description:
      'Ensures links have discernible text that describes their purpose.',
    impact: 'serious',
    wcagCriteria: [
      {
        id: '2.4.4',
        name: 'Link Purpose (In Context)',
        level: 'A',
        url: 'https://www.w3.org/WAI/WCAG21/Understanding/link-purpose-in-context.html',
      },
      {
        id: '4.1.2',
        name: 'Name, Role, Value',
        level: 'A',
        url: 'https://www.w3.org/WAI/WCAG21/Understanding/name-role-value.html',
      },
    ],
    whyItMatters: `Links without accessible names are problematic because:
- Screen readers may announce them as just "link" with no context
- Users can't understand where the link leads without visual cues
- Navigation becomes difficult when browsing links out of context
- Users scanning a page can't determine link destinations

This is especially critical for icon-only links and image links.`,
    howToFix: `Ensure every link has accessible text:

1. **Text content**: Include descriptive text between \`<a>\` tags
2. **Image links**: Add alt text to images inside links
3. **aria-label**: Use for icon-only links
4. **aria-labelledby**: Reference another element's text
5. **Visually hidden text**: Use CSS to hide text visually but keep it for screen readers

Avoid generic text like "click here", "read more", or "learn more" without context.`,
    codeExamples: [
      {
        title: 'Link with text',
        before: '<a href="/products"></a>',
        after: '<a href="/products">View all products</a>',
        language: 'html',
      },
      {
        title: 'Icon-only link with aria-label',
        before:
          '<a href="/cart">\n  <svg><!-- cart icon --></svg>\n</a>',
        after:
          '<a href="/cart" aria-label="Shopping cart (3 items)">\n  <svg aria-hidden="true"><!-- cart icon --></svg>\n</a>',
        language: 'html',
      },
      {
        title: 'Link with visually hidden text',
        before: '<a href="/article">Read more</a>',
        after:
          '<a href="/article">\n  Read more\n  <span class="sr-only"> about accessibility best practices</span>\n</a>',
        language: 'html',
      },
    ],
    externalResources: [
      {
        title: 'MDN: HTML anchor element',
        url: 'https://developer.mozilla.org/en-US/docs/Web/HTML/Element/a',
        source: 'MDN',
      },
      {
        title: 'WebAIM: Links and Hypertext',
        url: 'https://webaim.org/techniques/hypertext/',
        source: 'WebAIM',
      },
      {
        title: 'Deque: link-name',
        url: 'https://dequeuniversity.com/rules/axe/4.6/link-name',
        source: 'Deque',
      },
    ],
  },

  'button-name': {
    ruleId: 'button-name',
    title: 'Buttons must have discernible text',
    description:
      'Ensures buttons have discernible text that describes their purpose.',
    impact: 'critical',
    wcagCriteria: [
      {
        id: '4.1.2',
        name: 'Name, Role, Value',
        level: 'A',
        url: 'https://www.w3.org/WAI/WCAG21/Understanding/name-role-value.html',
      },
    ],
    whyItMatters: `Buttons without accessible names prevent users from understanding their function:
- Screen readers announce them as just "button" without describing the action
- Voice control users can't activate buttons by speaking their name
- Users can't predict what will happen when they click
- This is especially common with icon-only buttons

All interactive controls must have a programmatically determinable name.`,
    howToFix: `Provide an accessible name for every button:

1. **Text content**: Include descriptive text inside the button
2. **aria-label**: Use for icon-only buttons
3. **aria-labelledby**: Reference another element containing the button's name
4. **title attribute**: As a last resort (not well supported)

For icon buttons, ensure the icon is marked as \`aria-hidden="true"\` to prevent double announcements.`,
    codeExamples: [
      {
        title: 'Button with text',
        before: '<button type="submit"></button>',
        after: '<button type="submit">Submit form</button>',
        language: 'html',
      },
      {
        title: 'Icon button with aria-label',
        before:
          '<button>\n  <svg><!-- close icon --></svg>\n</button>',
        after:
          '<button aria-label="Close dialog">\n  <svg aria-hidden="true"><!-- close icon --></svg>\n</button>',
        language: 'html',
      },
      {
        title: 'Button with hidden text and icon',
        before:
          '<button class="menu-btn">\n  <svg><!-- menu icon --></svg>\n</button>',
        after:
          '<button class="menu-btn">\n  <svg aria-hidden="true"><!-- menu icon --></svg>\n  <span class="sr-only">Open navigation menu</span>\n</button>',
        language: 'html',
      },
    ],
    externalResources: [
      {
        title: 'MDN: HTML button element',
        url: 'https://developer.mozilla.org/en-US/docs/Web/HTML/Element/button',
        source: 'MDN',
      },
      {
        title: 'WAI-ARIA: Button Pattern',
        url: 'https://www.w3.org/WAI/ARIA/apg/patterns/button/',
        source: 'WCAG',
      },
      {
        title: 'Deque: button-name',
        url: 'https://dequeuniversity.com/rules/axe/4.6/button-name',
        source: 'Deque',
      },
    ],
  },

  'color-contrast': {
    ruleId: 'color-contrast',
    title: 'Elements must have sufficient color contrast',
    description:
      'Ensures the contrast between foreground and background colors meets WCAG 2 AA requirements.',
    impact: 'serious',
    wcagCriteria: [
      {
        id: '1.4.3',
        name: 'Contrast (Minimum)',
        level: 'AA',
        url: 'https://www.w3.org/WAI/WCAG21/Understanding/contrast-minimum.html',
      },
    ],
    whyItMatters: `Insufficient color contrast makes text difficult or impossible to read for:
- Users with low vision
- Users with color blindness
- Anyone using a device in bright sunlight
- Older users with declining vision

Poor contrast affects approximately 1 in 12 men and 1 in 200 women with color blindness,
plus millions more with low vision or situational impairments.`,
    howToFix: `Ensure text meets minimum contrast ratios:

**WCAG AA Requirements:**
- Normal text (under 18pt or 14pt bold): **4.5:1** ratio
- Large text (18pt+ or 14pt+ bold): **3:1** ratio
- UI components and graphics: **3:1** ratio

**Tips:**
- Use a contrast checker tool to verify ratios
- Avoid light gray text on white backgrounds
- Don't rely solely on color to convey information
- Test with color blindness simulators`,
    codeExamples: [
      {
        title: 'Improving text contrast',
        before:
          '.text-light {\n  color: #999999; /* Only 2.85:1 on white */\n  background: #ffffff;\n}',
        after:
          '.text-light {\n  color: #595959; /* 7:1 on white */\n  background: #ffffff;\n}',
        language: 'css',
      },
      {
        title: 'Fixing button contrast',
        before:
          '.btn-primary {\n  color: #ffffff;\n  background: #88ccff; /* 1.98:1 - fails */\n}',
        after:
          '.btn-primary {\n  color: #ffffff;\n  background: #0066cc; /* 7.02:1 - passes */\n}',
        language: 'css',
      },
      {
        title: 'Using CSS custom properties',
        before: ':root {\n  --text-secondary: #bbbbbb;\n}',
        after:
          ':root {\n  --text-secondary: #595959; /* Ensures 7:1 contrast */\n}',
        language: 'css',
      },
    ],
    externalResources: [
      {
        title: 'WebAIM Contrast Checker',
        url: 'https://webaim.org/resources/contrastchecker/',
        source: 'WebAIM',
      },
      {
        title: 'MDN: Color contrast',
        url: 'https://developer.mozilla.org/en-US/docs/Web/Accessibility/Understanding_WCAG/Perceivable/Color_contrast',
        source: 'MDN',
      },
      {
        title: 'Deque: color-contrast',
        url: 'https://dequeuniversity.com/rules/axe/4.6/color-contrast',
        source: 'Deque',
      },
      {
        title: 'Accessible Color Palette Generator',
        url: 'https://toolness.github.io/accessible-color-matrix/',
        source: 'Other',
      },
    ],
  },

  'heading-order': {
    ruleId: 'heading-order',
    title: 'Heading levels should only increase by one',
    description:
      'Ensures the order of headings is semantically correct and does not skip levels.',
    impact: 'moderate',
    wcagCriteria: [
      {
        id: '1.3.1',
        name: 'Info and Relationships',
        level: 'A',
        url: 'https://www.w3.org/WAI/WCAG21/Understanding/info-and-relationships.html',
      },
    ],
    whyItMatters: `Proper heading structure is crucial for navigation and comprehension:
- Screen reader users often navigate by headings to understand page structure
- Skipped heading levels create confusion about content hierarchy
- Search engines use headings to understand page content
- Users scanning the page rely on visual heading hierarchy

Headings should create a logical outline of the page content.`,
    howToFix: `Ensure heading levels increase sequentially:

- Start with \`<h1>\` for the main page title (usually one per page)
- Use \`<h2>\` for major sections
- Use \`<h3>\` for subsections within \`<h2>\` sections
- Never skip levels (e.g., \`<h2>\` to \`<h4>\`)
- Don't use headings just for styling - use CSS instead
- It's okay to skip levels when going back up (e.g., \`<h4>\` back to \`<h2>\`)`,
    codeExamples: [
      {
        title: 'Correct heading hierarchy',
        before:
          '<h1>Page Title</h1>\n<h3>First Section</h3> <!-- Skips h2! -->\n<h5>Subsection</h5> <!-- Skips h4! -->',
        after:
          '<h1>Page Title</h1>\n<h2>First Section</h2>\n<h3>Subsection</h3>',
        language: 'html',
      },
      {
        title: 'Using CSS for visual styling instead of headings',
        before:
          '<h4 class="small-heading">Not actually a subsection</h4>',
        after:
          '<p class="font-semibold text-lg">Not actually a subsection</p>',
        language: 'html',
      },
    ],
    externalResources: [
      {
        title: 'MDN: HTML heading elements',
        url: 'https://developer.mozilla.org/en-US/docs/Web/HTML/Element/Heading_Elements',
        source: 'MDN',
      },
      {
        title: 'WebAIM: Headings',
        url: 'https://webaim.org/techniques/semanticstructure/#headings',
        source: 'WebAIM',
      },
      {
        title: 'Deque: heading-order',
        url: 'https://dequeuniversity.com/rules/axe/4.6/heading-order',
        source: 'Deque',
      },
    ],
  },

  'aria-valid-attr': {
    ruleId: 'aria-valid-attr',
    title: 'ARIA attributes must conform to valid names',
    description:
      'Ensures attributes that begin with aria- are valid ARIA attributes.',
    impact: 'critical',
    wcagCriteria: [
      {
        id: '4.1.2',
        name: 'Name, Role, Value',
        level: 'A',
        url: 'https://www.w3.org/WAI/WCAG21/Understanding/name-role-value.html',
      },
    ],
    whyItMatters: `Invalid ARIA attributes are ignored by assistive technologies, which means:
- The intended accessibility enhancement won't work
- Screen readers may misinterpret the element's purpose
- Users may miss important information or functionality
- It often indicates a typo or misunderstanding of ARIA`,
    howToFix: `Verify all ARIA attributes are spelled correctly and exist in the ARIA specification:

**Common mistakes:**
- \`aria-role\` should be just \`role\`
- \`aria-labeledby\` should be \`aria-labelledby\`
- \`aria-decribedby\` should be \`aria-describedby\`

**Resources:**
- Use your IDE's autocomplete for ARIA attributes
- Reference the WAI-ARIA specification
- Use axe DevTools to catch invalid attributes`,
    codeExamples: [
      {
        title: 'Fixing typo in aria-labelledby',
        before: '<div aria-labeledby="title">Content</div>',
        after: '<div aria-labelledby="title">Content</div>',
        language: 'html',
      },
      {
        title: 'Fixing invalid aria-role',
        before: '<div aria-role="button">Click me</div>',
        after: '<div role="button" tabindex="0">Click me</div>',
        language: 'html',
      },
      {
        title: 'Fixing aria-describedby typo',
        before:
          '<input aria-decribedby="helper" />\n<span id="helper">Enter your email</span>',
        after:
          '<input aria-describedby="helper" />\n<span id="helper">Enter your email</span>',
        language: 'html',
      },
    ],
    externalResources: [
      {
        title: 'WAI-ARIA Specification',
        url: 'https://www.w3.org/TR/wai-aria-1.2/',
        source: 'WCAG',
      },
      {
        title: 'MDN: ARIA',
        url: 'https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA',
        source: 'MDN',
      },
      {
        title: 'Deque: aria-valid-attr',
        url: 'https://dequeuniversity.com/rules/axe/4.6/aria-valid-attr',
        source: 'Deque',
      },
    ],
  },

  'list': {
    ruleId: 'list',
    title: '<ul> and <ol> must only directly contain <li>, <script> or <template> elements',
    description:
      'Ensures that lists are structured correctly.',
    impact: 'serious',
    wcagCriteria: [
      {
        id: '1.3.1',
        name: 'Info and Relationships',
        level: 'A',
        url: 'https://www.w3.org/WAI/WCAG21/Understanding/info-and-relationships.html',
      },
    ],
    whyItMatters: `Screen readers use proper list structure to help users navigate content:
- They announce "list of X items" when entering a list
- Users can skip from item to item
- Improper structure breaks this navigation
- The semantic meaning of grouped items is lost`,
    howToFix: `Ensure list elements are properly nested:

- \`<ul>\` and \`<ol>\` can only contain \`<li>\` elements as direct children
- All list content must be inside \`<li>\` elements
- If you need to wrap items, use a containing element around the whole list
- For non-list content styled like a list, use CSS or ARIA roles instead`,
    codeExamples: [
      {
        title: 'Proper list structure',
        before:
          '<ul>\n  <div>Item 1</div>\n  <div>Item 2</div>\n</ul>',
        after:
          '<ul>\n  <li>Item 1</li>\n  <li>Item 2</li>\n</ul>',
        language: 'html',
      },
      {
        title: 'Moving wrapper inside list item',
        before:
          '<ul>\n  <a href="#">\n    <li>Link item</li>\n  </a>\n</ul>',
        after:
          '<ul>\n  <li>\n    <a href="#">Link item</a>\n  </li>\n</ul>',
        language: 'html',
      },
    ],
    externalResources: [
      {
        title: 'MDN: HTML ul element',
        url: 'https://developer.mozilla.org/en-US/docs/Web/HTML/Element/ul',
        source: 'MDN',
      },
      {
        title: 'WebAIM: Lists',
        url: 'https://webaim.org/techniques/semanticstructure/#lists',
        source: 'WebAIM',
      },
      {
        title: 'Deque: list',
        url: 'https://dequeuniversity.com/rules/axe/4.6/list',
        source: 'Deque',
      },
    ],
  },
};

/**
 * Get fix guidance for a rule ID
 */
export function getFixGuidance(ruleId: string): FixGuidance | null {
  return fixGuidanceData[ruleId] || null;
}

/**
 * Get all available rule IDs with fix guidance
 */
export function getAvailableRuleIds(): string[] {
  return Object.keys(fixGuidanceData);
}
